import moment from "moment";
import "moment-duration-format";

const powerFeePerUnit = 4;
export const computePowerUnit = (previousData, currentData) => {
  let powerUsage = 0;
  let diffTime = 0;
  if (previousData !== undefined) {
    const currentTime = moment(currentData["time"]);
    const previousTime = moment(previousData["time"]);
    diffTime = moment.duration(currentTime.diff(previousTime)).asHours();
  }
  if (currentData["power"] !== undefined) {
    powerUsage = currentData["power"];
  }
  return computeKWUnitUse(powerUsage, diffTime);
};
export const computeKWUnitUse = (powerUsage, hoursDuration) => {
  return (powerUsage * hoursDuration) / 1000;
};
export const computePowerCost = (kwh) => {
  return kwh * powerFeePerUnit;
};

// Grid-average emission factor for Thailand (kgCO2e per kWh). Single national
// value for now (per CORNTROL-479); a per-device/plan EF from the backend can
// replace this later without touching call sites.
export const EMISSION_FACTOR = 0.399;
export const computeCarbonFootprint = (kwh) => kwh * EMISSION_FACTOR;

// Total/avg/max/min kWh + cost over MINUTE-LEVEL rows — the single source the
// PowerDetail summary and the DeviceDetail2 quick-view power cards both use,
// so their numbers can never disagree. Each step integrates power (W) over the
// gap to the previous row. NEVER feed per-day snapshot rows in here: one
// instantaneous reading would be multiplied by 24 hours.
export const sumPowerUsage = (rows) => {
  let totalKWH = 0;
  let maxKWH = 0;
  let minKWH = 0;
  (rows ?? []).forEach((e, index) => {
    const currentKWH = computePowerUnit(rows[index - 1], rows[index]);
    if (maxKWH < currentKWH) maxKWH = currentKWH;
    if (minKWH > currentKWH) minKWH = currentKWH;
    totalKWH += currentKWH;
  });
  return {
    electricityUnit: totalKWH,
    electricityAVGUnit: (rows ?? []).length > 0 ? totalKWH / rows.length : 0,
    electricityMaxUnit: maxKWH,
    electricityMinUnit: minKWH,
    electricityBill: computePowerCost(totalKWH),
    electricityCarbon: computeCarbonFootprint(totalKWH),
  };
};
export const computeSumOperationRecordUsed = (
  currentDeviceReport,
  previousDeviceReport,
  dateTimeUnit
) => {
  let compressorPeriodUseInHour = 0;
  let defrostPeriodUseInHour = 0;
  let fanPeriodUseInHour = 0;
  let activePeriodUseInHour = 0;

  compressorPeriodUseInHour = computePeriodOfUse(
    previousDeviceReport,
    currentDeviceReport,
    "comp",
    dateTimeUnit
  );
  defrostPeriodUseInHour = computePeriodOfUse(
    previousDeviceReport,
    currentDeviceReport,
    "defrost",
    dateTimeUnit
  );
  fanPeriodUseInHour = computePeriodOfUse(
    previousDeviceReport,
    currentDeviceReport,
    "fan",
    dateTimeUnit
  );
  activePeriodUseInHour = computePeriodOfUse(
    previousDeviceReport,
    currentDeviceReport,
    "active",
    dateTimeUnit
  );

  return {
    compressorPeriodUseInHourResult: compressorPeriodUseInHour,
    defrostPeriodUseInHourResult: defrostPeriodUseInHour,
    fanPeriodUseInHourResult: fanPeriodUseInHour,
    activePeriodUseInHourResult: activePeriodUseInHour,
  };
};
export const computePeriodOfUse = (
  previousData,
  currentData,
  fieldKey,
  unitTime
) => {
  if (previousData !== undefined) {
    const currentTime = moment(currentData["time"]);
    const previousTime = moment(previousData["time"]);
    const diffTime = moment
      .duration(currentTime.diff(previousTime))
      .asMilliseconds();
    if (currentData[fieldKey] !== false) {
      return diffTime;
    }
  } else {
    if (currentData[fieldKey] !== false) {
      return 60000;
    }
  }
  return 0
};
export const formatDuration = (milliseconds) => {
  // return milliseconds > 0
  //   ? moment.duration(milliseconds).format("h:mm [hrs]", { trim: false })
  //   : "0:00 hrs";
  return milliseconds > 0
    ? moment.duration(milliseconds).format("h:mm", { trim: false })
    : "0:00";
};
// Sum device runtime over MINUTE-LEVEL rows (records for comp/defrost/fan/
// active, events for the door) — the single source both the device-detail
// quick view and OperationDetail use, so their hour cards can never disagree.
// NEVER feed per-day snapshot rows in here: a day whose snapshot happened to
// read "on" would count as 24 hours of runtime. Returns milliseconds (+ the
// door-open count); format with formatDuration at the call site.
export const sumOperationUsage = (recordRows, eventRows, dateTimeUnit) => {
  let compressorMs = 0;
  let defrostMs = 0;
  let fanMs = 0;
  let activeMs = 0;
  let doorOpenMs = 0;
  let doorOpenCount = 0;
  (recordRows ?? []).forEach((e, index) => {
    const {
      compressorPeriodUseInHourResult,
      defrostPeriodUseInHourResult,
      fanPeriodUseInHourResult,
      activePeriodUseInHourResult,
    } = computeSumOperationRecordUsed(
      recordRows[index],
      recordRows[index - 1],
      dateTimeUnit
    );
    compressorMs += compressorPeriodUseInHourResult;
    defrostMs += defrostPeriodUseInHourResult;
    fanMs += fanPeriodUseInHourResult;
    activeMs += activePeriodUseInHourResult;
  });
  (eventRows ?? []).forEach((e, index) => {
    const { doorOpenPeriodInHourResult, doorOpenTimeResult } =
      computeSumOperationEventUsed(eventRows[index], eventRows[index - 1]);
    doorOpenMs += doorOpenPeriodInHourResult;
    doorOpenCount += doorOpenTimeResult;
  });
  return {
    compressorMs,
    defrostMs,
    fanMs,
    activeMs,
    doorOpenMs,
    doorOpenCount,
  };
};

export const computeSumOperationEventUsed = (
  currentDeviceReport,
  previousDeviceReport
) => {
  let doorOpenPeriodInHour = 0;
  let doorOpenTime = 0;

  doorOpenPeriodInHour = computePeriodDoorOpen(
    previousDeviceReport,
    currentDeviceReport
  );
  doorOpenTime = computeDoorOpenTime(
    previousDeviceReport,
    currentDeviceReport,
    "door"
  );

  return {
    doorOpenPeriodInHourResult: doorOpenPeriodInHour,
    doorOpenTimeResult: doorOpenTime,
  };
};
export const computePeriodDoorOpen = (previousData, currentData) => {
  if (previousData !== undefined) {
    const currentTime = moment(currentData["time"]);
    const previousTime = moment(previousData["time"]);
    if (currentData["door"] === false && previousData["door"] === true) {
      const diffTime = moment
        .duration(currentTime.diff(previousTime))
        .asMilliseconds();

      return diffTime;
    }
  }
  return 0;
};
export const computeDoorOpenTime = (previousData, currentData) => {
  if (previousData !== undefined) {
    if (currentData["door"] === true) {
      return 1;
    }
  }
  return 0;
};
